// TODO(RBAC, parent #641 #647): admin only
import { useEffect, useState } from 'react'

import { create } from '@bufbuild/protobuf'
import { timestampDate } from '@bufbuild/protobuf/wkt'
import { Code, ConnectError } from '@connectrpc/connect'
import {
  LinkedPageKind,
  type WorkflowCaseTag,
  WorkflowSection,
} from '@ebalance/proto/bffuser/v1/workflow_template_model_pb'
import {
  CreateWorkflowTemplateRequestSchema,
  UpdateWorkflowTemplateRequestSchema,
} from '@ebalance/proto/bffuser/v1/workflow_template_service_pb'
import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import type { HTMLChakraProps } from '@enechain/ecloud-designsystem'
import {
  Alert,
  Box,
  FieldRoot,
  Flex,
  Grid,
  Input,
  PrimaryButton,
  FieldErrorText as RawFieldErrorText,
  FieldLabel as RawFieldLabel,
  TertiaryButton,
  Text,
  VStack,
  fontSizes,
} from '@enechain/ecloud-designsystem'

import { AuditPanel } from '~/components/common/AuditPanel'
import { FormSelect } from '~/components/common/FormSelect'
import { RegionLoading } from '~/components/common/RegionLoading'
import { WorkflowCaseTagEditor } from '~/components/common/WorkflowCaseTagEditor'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { codePointLength, truncateToCodePointLength } from '~/libs/codePoint'
import { resolveFormErrorAlertMessage } from '~/libs/formErrors'
import {
  WORKFLOW_SECTION_ITEMS,
  canonicalWorkflowSection,
} from '~/models/workflowSection'
import { BigStepEditor } from '~/pages/AdminWorkflowTemplates/components/BigStepEditor'
import { useCreateWorkflowTemplate } from '~/pages/AdminWorkflowTemplates/hooks/useCreateWorkflowTemplate'
import { useGetWorkflowTemplate } from '~/pages/AdminWorkflowTemplates/hooks/useGetWorkflowTemplate'
import { useGetWorkflowTemplateFormOptions } from '~/pages/AdminWorkflowTemplates/hooks/useGetWorkflowTemplateFormOptions'
import type { WorkflowTemplateRow } from '~/pages/AdminWorkflowTemplates/hooks/useListWorkflowTemplates'
import { useUpdateWorkflowTemplate } from '~/pages/AdminWorkflowTemplates/hooks/useUpdateWorkflowTemplate'
import {
  approvalSettingFromProto,
  approvalSettingToProto,
} from '~/pages/AdminWorkflowTemplates/models/approvalSetting'
import { planAutoSubmitOffsetForWire } from '~/pages/AdminWorkflowTemplates/models/planAutoSubmitOffset'
import { planCreationTimeForWire } from '~/pages/AdminWorkflowTemplates/models/planCreationTime'
import {
  type WorkflowTemplateFormValues,
  createWorkflowTemplateFormSchema,
} from '~/pages/AdminWorkflowTemplates/models/schema'

/**
 * chakra v3 の `FieldLabelProps` / `FieldErrorTextProps` は本プロジェクトの型解決で
 * `HTMLChakraProps` が collapse し `children` / style props が欠落する
 * (= ecloud 0.14 / chakra 3.29 の d.ts 解決問題)。 v3 本来の prop シグネチャ
 * (`HTMLChakraProps<'label'|'div'>`) へ cast して復元する。 ランタイムは ecloud のまま。
 */
const FieldLabel = RawFieldLabel as React.FC<HTMLChakraProps<'label'>>
const FieldErrorText = RawFieldErrorText as React.FC<HTMLChakraProps<'div'>>

/**
 * 計画断面は 4 区分を表示するが、P0 で選べるのは単日のみ。
 */
const TEMPLATE_SECTION_ITEMS = WORKFLOW_SECTION_ITEMS

const isSelectableTemplateSection = (section: WorkflowSection): boolean =>
  section === WorkflowSection.SINGLE_DAY

type Props = {
  /** 編集対象の行。 undefined の場合は新規作成 (= 空 form or 複製モード) */
  row: WorkflowTemplateRow | undefined
  /** URL から直接開いた編集対象。行が未取得でも既存テンプレートとして扱う。 */
  templateId?: string
  /**
   * 複製元の行 (= 「複製して新規」 経由)。 値があれば form に複製元データを populate し、 id 無しで Create する。
   * row が指定されている場合 (= 編集モード) は無視する。
   */
  sourceRow?: WorkflowTemplateRow | undefined
  onClose: () => void
  /**
   * create/update mutation の pending (= isPending) が変わるたびに親へ通知する。
   * 親 (index.tsx) はこれを使ってパンくずの中間項目を保存中は非リンク化し、
   * キャンセルボタン (disabled={isPending}) と挙動を揃える (issue #7007 review 指摘:
   * 保存中にパンくずから離脱できると、保存失敗時のエラー表示・入力内容が
   * アンマウントで失われる)。
   */
  onPendingChange?: (pending: boolean) => void
}

/**
 * ワークフロー雛形 新規作成 / 編集フォーム。
 *
 * 編集時は useGetWorkflowTemplate で full entity を取得して form を populate する。
 * optimistic locking: Get response の updated_at を expected_updated_at として送る
 * (= proto Timestamp を直渡し、JS Date 変換で µs 精度損失を起こさない)。
 *
 * #1: schedule フィールドは廃止済み。 populate / submit から除去。
 * #2: assigneeMemberIds (multi-select 化: issue #4550) を SubStep に追加。
 * members / 「作成済のタグ」再利用サジェスト用プール / 承認フロー設定は
 * useGetWorkflowTemplateFormOptions の集約 RPC 1 本で取得する
 * (workflow-template-form-options-bff-aggregation design。 editor は
 * isFormMode 時のみ mount されるため、 このフォーム表示中だけ取得される)。
 * section は必須。P0 では単日だけを選択可能にする。
 */
export const WorkflowTemplateEdit = ({
  row,
  templateId,
  sourceRow,
  onClose,
  onPendingChange,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const showSuccessToast = useSuccessToast()
  const editTemplateId = row?.id ?? templateId
  const isCreate = editTemplateId === undefined
  const isDuplicate = isCreate && sourceRow !== undefined
  const fetchSourceId = editTemplateId ?? sourceRow?.id ?? ''

  const { mutateAsync: createAsync, isPending: isCreating } =
    useCreateWorkflowTemplate()
  const { mutateAsync: updateAsync, isPending: isUpdating } =
    useUpdateWorkflowTemplate()
  const isPending = isCreating || isUpdating

  // 保存中を親へ通知する (props JSDoc 参照)。
  useEffect(() => {
    onPendingChange?.(isPending)
  }, [isPending, onPendingChange])

  const {
    data: getResponse,
    isPending: isFetching,
    isError: isFetchError,
  } = useGetWorkflowTemplate(fetchSourceId)

  // #2: 担当者一覧 + 「作成済のタグ」プール + 承認フロー設定を集約 RPC 1 本で取得
  // (workflow-template-form-options-bff-aggregation design AC-1)。
  // members / approvalProcesses は BigStepEditor 経由で SubStepEditor へ
  // down-thread、 tagPool は WorkflowCaseTagEditor の suggestedTags にそのまま渡す。
  const { data: formOptionsResponse } = useGetWorkflowTemplateFormOptions()
  const members = formOptionsResponse?.members ?? []
  const suggestedTags = formOptionsResponse?.tagPool
  const approvalProcesses = formOptionsResponse?.approvalProcesses

  const {
    control,
    handleSubmit,
    reset,
    clearErrors,
    setError,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<WorkflowTemplateFormValues>({
    resolver: zodResolver(createWorkflowTemplateFormSchema(t)),
    mode: 'onBlur',
    defaultValues: {
      id: undefined,
      name: '',
      description: undefined,
      section: undefined,
      bigSteps: [],
    },
  })

  // AC-10: デフォルトタグ。 react-hook-form の schema には含めない (= 他の複雑な値
  // 同様、 proto message 配列を直接保持する useState で管理する)。
  const [defaultTags, setDefaultTags] = useState<WorkflowCaseTag[]>([])
  // AC-TAG-009 (workflow-tag-delete-ui design Decision A/C): 「作成済のタグ」
  // ボックスの編集モードで削除予定にした label の集合。既存雛形の編集
  // (!isCreate) でのみ意味を持つ — 新規作成には removed_tag_labels 自体が無い
  // (Contract: UpdateWorkflowTemplateRequest のみが持つフィールド)。
  const [removedTagLabels, setRemovedTagLabels] = useState<string[]>([])

  // Get response が返ったら form を populate（別行を編集した場合も含む）。
  const fetched = getResponse?.template
  useEffect(() => {
    if (fetched !== undefined) {
      // 複製モードでも複製元のデフォルトタグを引き継ぐ (name と同じ populate 対象)。
      setDefaultTags(fetched.defaultTags)
      const copySuffix = ` ${t('page:adminWorkflowTemplates.form.copySuffix')}`
      reset({
        id: isDuplicate ? undefined : fetched.id,
        name: isDuplicate
          ? `${truncateToCodePointLength(
              fetched.name,
              30 - codePointLength(copySuffix),
            )}${copySuffix}`
          : fetched.name,
        description: fetched.description ?? '',
        // #1: schedule は廃止済み、 populate しない
        // 旧当日系は単日へ寄せ、P0 で選べない断面は未選択にする。
        section: isSelectableTemplateSection(
          canonicalWorkflowSection(fetched.section),
        )
          ? canonicalWorkflowSection(fetched.section)
          : undefined,
        bigSteps: fetched.bigSteps.map((b) => ({
          displayOrder: b.displayOrder,
          title: b.title,
          subSteps: b.subSteps.map((s) => {
            const linkedPageKind =
              s.linkedPageKind !== undefined &&
              s.linkedPageKind !== LinkedPageKind.UNSPECIFIED
                ? s.linkedPageKind
                : undefined
            const approvalSetting = approvalSettingFromProto(
              linkedPageKind,
              s.approvalSetting,
            )
            return {
              displayOrder: s.displayOrder,
              name: s.name,
              description: s.description ?? '',
              linkedPageKind,
              // #2 (multi-select 化: issue #4550): assigneeMemberIds をそのまま populate
              assigneeMemberIds: s.assigneeMemberIds,
              approvalSetting,
              demandAutoConfirmMethod: s.demandAutoConfirmMethod,
              demandAutoConfirmOtherMethod: s.demandAutoConfirmOtherMethod,
              planAutoSubmitOffsetMinutes: s.planAutoSubmitOffsetMinutes,
              planCreationTime:
                s.planCreationTime === undefined
                  ? undefined
                  : {
                      daysBefore: s.planCreationTime.daysBefore,
                      localTime: s.planCreationTime.localTime,
                    },
              originalAutomationContext: {
                linkedPageKind,
                approvalMode: approvalSetting?.mode,
              },
            }
          }),
        })),
      })
    }
  }, [fetched, isDuplicate, reset, t])

  const handleClose = (): void => {
    reset()
    setDefaultTags([])
    setRemovedTagLabels([])
    onClose()
  }

  const onSubmit = async (
    values: WorkflowTemplateFormValues,
  ): Promise<void> => {
    try {
      const normalizedBigSteps = values.bigSteps.map((b, bIdx) => ({
        ...b,
        displayOrder: bIdx + 1,
        subSteps: b.subSteps.map((s, sIdx) => {
          const {
            originalAutomationContext,
            forecastMethodInputContext,
            ...subStep
          } = s
          const kind =
            s.linkedPageKind === LinkedPageKind.UNSPECIFIED
              ? undefined
              : s.linkedPageKind
          const automationContextChanged = [
            originalAutomationContext,
            forecastMethodInputContext,
          ].some(
            (context) =>
              context !== undefined &&
              (context.linkedPageKind !== kind ||
                context.approvalMode !== s.approvalSetting?.mode),
          )
          return {
            ...subStep,
            displayOrder: sIdx + 1,
            description: s.description !== '' ? s.description : undefined,
            // UNSPECIFIED (= 0) は「リンクなし」で undefined と等価 (schema JSDoc)。
            // form 側は「リンクなし」を 0 で保持する (React Hook Form が undefined の
            // field を defaultValue へフォールバックさせるため、 undefined では
            // リンク先を外す操作が成立しない) ので、 送信時にここで元の形へ戻す。
            linkedPageKind:
              s.linkedPageKind !== LinkedPageKind.UNSPECIFIED
                ? s.linkedPageKind
                : undefined,
            // #2 (multi-select 化: issue #4550): assigneeMemberIds を submit
            // (空配列 = 「担当者なし」)
            assigneeMemberIds: s.assigneeMemberIds,
            approvalSetting: approvalSettingToProto(s.approvalSetting),
            // The backend owns whether an automation value can be held by a
            // given link / approval mode. This marker records only a user
            // edit of this row's context; the values themselves stay on the
            // full PUT so the backend can validate or discard them safely.
            discardIncompatibleInheritedAutomationFields:
              automationContextChanged,
            demandAutoConfirmMethod: s.demandAutoConfirmMethod,
            demandAutoConfirmOtherMethod:
              s.demandAutoConfirmOtherMethod ?? undefined,
            // The raw input draft is form-only; the wire carries the resolved value.
            planAutoSubmitOffsetInput: undefined,
            planAutoSubmitOffsetMinutes: planAutoSubmitOffsetForWire(s),
            planCreationTimeInput: undefined,
            planCreationTime: planCreationTimeForWire(s),
          }
        }),
      }))

      if (isCreate) {
        await createAsync(
          create(CreateWorkflowTemplateRequestSchema, {
            template: {
              name: values.name,
              description:
                values.description !== '' ? values.description : undefined,
              // #1: schedule は廃止済み → UNSPECIFIED で送る (proto optional)
              section: values.section ?? WorkflowSection.UNSPECIFIED,
              isBuiltIn: false,
              bigSteps: normalizedBigSteps,
              defaultTags,
            },
          }),
        )
      } else {
        await updateAsync(
          create(UpdateWorkflowTemplateRequestSchema, {
            template: {
              id: editTemplateId,
              name: values.name,
              description:
                values.description !== '' ? values.description : undefined,
              // #1: schedule 廃止済み → UNSPECIFIED
              section: values.section ?? WorkflowSection.UNSPECIFIED,
              isBuiltIn: fetched?.isBuiltIn ?? false,
              bigSteps: normalizedBigSteps,
              defaultTags,
            },
            // 未上書きの初期雛形は updatedAt を持たない (backend が
            // DefaultWorkflowTemplates を Audit 空で返すため)。この場合
            // expectedUpdatedAt を省略し (proto optional)、backend の初回
            // overlay 経路 (楽観ロックなし INSERT) へ通す (issue #6356)。
            // updatedAt がある通常の雛形は proto Timestamp を直接 round-trip
            // (JS Date 変換で µs 精度損失を起こさない)。
            expectedUpdatedAt: fetched?.updatedAt,
            removedTagLabels,
            automationFieldsSupported: true,
            planCreationTimeSupported: true,
          }),
        )
      }

      reset()
      setDefaultTags([])
      setRemovedTagLabels([])
      onClose()
      // 2026-08-28 デザイナー指摘「削除・追加・編集の成功時は全画面共通で toast」。
      // 対象語は付けない — この画面の編集対象は雛形 1 種類なので
      // (辞書の実測: 対象語を持つ成功文言はいずれも複数対象が同居する画面)。
      showSuccessToast({ title: t('common:toast.saved') })
    } catch (err) {
      // AC-TAG-006 (tag-pool-limit-and-suggest design): frontend の 20 個
      // 上限事前チェック (WorkflowCaseTagEditor) は同一セッション内の複数
      // 追加やプール未取得中はすり抜けうる。backend の最終判定
      // (ResourceExhausted) を固定文言へ写像し、汎用 saveFailed に落ちて
      // 無言 fail する事故を防ぐ。
      if (err instanceof ConnectError && err.code === Code.ResourceExhausted) {
        setError('root.serverError', {
          message: t('common:tag.poolLimitError'),
        })
      } else if (err instanceof ConnectError && err.code === Code.Aborted) {
        // 楽観ロック衝突 (別ユーザーが先に更新 / 未上書き初期雛形を開いている
        // 間に overlay された場合の baseline 不一致・省略)。時間経過では解消
        // しないため、汎用 saveFailed でなく「再読み込みして再試行」を促す
        // (issue #6356 PR #6393 review C3)。
        setError('root.serverError', {
          message: t('page:adminWorkflowTemplates.serverError.aborted'),
        })
      } else {
        setError('root.serverError', {
          message: t('page:adminWorkflowTemplates.serverError.saveFailed'),
        })
      }
    }
  }

  const needsFetch = !isCreate || isDuplicate
  const isLoading = needsFetch && isFetching && fetched === undefined

  const handleSave = (): void => {
    void handleSubmit(onSubmit, (formErrors) => {
      // 項目脇に FieldErrorText が出るエラーは alert に再掲しない (重複表示になる)。
      const alertMessage = resolveFormErrorAlertMessage(
        formErrors,
        t('page:adminWorkflowTemplates.serverError.validation'),
      )
      if (alertMessage !== undefined) {
        setError('root.serverError', { message: alertMessage })
      } else {
        // 直前の失敗で出した alert が残らないよう明示的に消す
        // (項目脇の表示だけに切り替わるため)。
        clearErrors('root.serverError')
      }
    })()
  }

  const panelTitle = isCreate
    ? t('page:adminWorkflowTemplates.action.create')
    : t('page:adminWorkflowTemplates.form.editTitle')

  // 保存 / キャンセルボタン (design: card-header と ct-form-actions の両方に配置)
  const saveDisabled = needsFetch && (isFetching || isFetchError)
  const cancelButton = (
    <TertiaryButton disabled={isPending} onClick={handleClose}>
      {t('common:action.cancel')}
    </TertiaryButton>
  )
  const saveButton = (
    <PrimaryButton
      disabled={saveDisabled}
      loading={isPending}
      onClick={handleSave}
    >
      {t('common:action.save')}
    </PrimaryButton>
  )

  return (
    // design .card .ct-edit-card
    <Box
      // PageScrollArea (flex column + overflow auto) の子として置かれるため、
      // flexShrink 既定値 (1) のままだと overflow:hidden により automatic minimum size が
      // 0 になり、 カードが残余高さまで潰されて中身がクリップされる (= 外側もスクロール
      // しない)。 design .ct-edit-card はカードごとページスクロールする形なので、
      // 収縮を止めて PageScrollArea に全体をスクロールさせる。
      flexShrink={0}
      overflow="hidden"
      background="Surface.primary"
      borderWidth="1px"
      borderColor="Border.lowEmphasis"
      borderRadius="lg"
      boxShadow="shadSm"
    >
      {/* card-header: タイトル(flex:1) + キャンセル + 保存 */}
      <Flex
        alignItems="center"
        flexWrap="wrap"
        gap="12px" // space-3
        padding="16px" // space-4
        borderBottomWidth="1px"
        borderBottomColor="Border.lowEmphasis"
      >
        <Text
          as="h2"
          flex={1}
          margin={0}
          color="Text.highEmphasis"
          // lg(16px)/bold(700) の組は role と不一致 (sectionTitle は lg/semibold) のため raw 維持。
          style={{ fontSize: fontSizes.lg.value, fontWeight: 700 }}
        >
          {panelTitle}
        </Text>
        {cancelButton}
        {saveButton}
      </Flex>

      {/* card-body */}
      <VStack align="stretch" gap={4} padding="16px">
        {/* サーバーエラー / バリデーションエラー表示 */}
        {errors.root?.serverError !== undefined && (
          <Alert
            whiteSpace="pre-line"
            status="error"
            title={errors.root.serverError.message}
          />
        )}
        {isFetchError && (
          <Alert
            description={t(
              'page:adminWorkflowTemplates.serverError.unknownDesc',
            )}
            status="error"
            title={t('page:adminWorkflowTemplates.serverError.unknownTitle')}
          />
        )}

        <RegionLoading isLoading={isLoading}>
          {isLoading ? null : (
            <>
              {/* 基本情報グリッド (雛形名 span-2 + 計画断面 + デフォルトタグ外枠の 3 列構成) */}
              <Grid gridGap="12px" gridTemplateColumns="repeat(3, 1fr)">
                {/* 雛形名（span 2） */}
                <FieldRoot
                  alignItems="stretch"
                  gridColumn="span 2"
                  invalid={errors.name !== undefined}
                >
                  {/* textStyle="label" = md/semibold で意図的視覚変更 (12px→14px)。
                    design case-templates.html:657-659 `.form-label` = md/600 が正、
                    ユーザー承認済み。 */}
                  <FieldLabel textStyle="label" htmlFor="workflowTemplate-name">
                    {t('page:adminWorkflowTemplates.form.name')}{' '}
                    <Text as="span" color="Text.negativeHigh">
                      *
                    </Text>
                  </FieldLabel>
                  {/* design case-templates.html:1338 の雛形名はサイズ修飾子なしの `.ecl-input`
                    = 高さ 40px / font 14px (components.css:2785-2790 + tokens.css:173)。
                    隣の計画断面 select (EnumSelect 既定 md = 40px) とも揃うので `size="md"`。
                    文字サイズは size では決まらない (chakra input recipe は md でも
                    `textStyle: 'sm'` = ecloud の 12px を渡す) ため、 EnumSelect.tsx と同様に
                    `textStyle="md"` を明示する。 */}
                  <Controller
                    control={control}
                    name="name"
                    render={({ field: f }) => (
                      <Input
                        {...f}
                        textStyle="md"
                        width="100%"
                        id="workflowTemplate-name"
                        onChange={(e) => {
                          f.onChange(
                            truncateToCodePointLength(e.target.value, 30),
                          )
                        }}
                        placeholder={t(
                          'page:adminWorkflowTemplates.form.namePlaceholder',
                        )}
                        size="md"
                      />
                    )}
                  />
                  <FieldErrorText>{errors.name?.message}</FieldErrorText>
                </FieldRoot>

                {/* 計画断面 (#4/#5: 必須・3 値) */}
                <FieldRoot invalid={errors.section !== undefined}>
                  {/* textStyle="label" = md/semibold で意図的視覚変更 (12px→14px)。
                    design case-templates.html:657-659 `.form-label` = md/600 が正、
                    ユーザー承認済み。 */}
                  <FieldLabel textStyle="label">
                    {t('page:adminWorkflowTemplates.form.section')}{' '}
                    <Text as="span" color="Text.negativeHigh">
                      *
                    </Text>
                  </FieldLabel>
                  {/* proto enum (number) を form に戻す変換は FormSelect の `numeric`
                    (= 共通 helper `enumSetValueAs`) に委ねる。 */}
                  <FormSelect
                    aria-label={t('page:adminWorkflowTemplates.form.section')}
                    control={control}
                    name="section"
                    numeric
                    options={TEMPLATE_SECTION_ITEMS.map((item) => {
                      const disabled = !isSelectableTemplateSection(item.value)
                      return {
                        value: String(item.value),
                        label: t(item.adminLabelKey),
                        disabled,
                        disabledReason: disabled
                          ? t(
                              'page:adminWorkflowTemplates.form.sectionUnavailableReason',
                            )
                          : undefined,
                      }
                    })}
                    placeholder={t(
                      'page:adminWorkflowTemplates.form.sectionPlaceholder',
                    )}
                  />
                  <FieldErrorText>{errors.section?.message}</FieldErrorText>
                </FieldRoot>

                {/*
                design `case-templates.html` のフォームには雛形レベルの「説明」入力が
                無いため UI からは除去。 値は schema/populate/submit で round-trip し続ける
                (= 既存テンプレの description を保存時に失わない)。
              */}
              </Grid>

              {/* AC-10: デフォルトタグ (色スウォッチ + テキスト入力で追加・削除) */}
              <FieldRoot alignItems="stretch">
                <FieldLabel textStyle="label">
                  {t('page:adminWorkflowTemplates.form.defaultTags')}
                </FieldLabel>
                {/* design case-templates.html:140 `.ct-tag-input { max-width:280px }`。
                  雛形編集のタグ行は全幅カード直下なので、上限が無いと入力欄が
                  「追加」ボタン直前まで伸びる (modal / ケース詳細は design 側も上限なし)。
                  AC-TAG-009: 編集モード (removed_tag_labels) は既存雛形の編集
                  (!isCreate) でのみ配線する — Create RPC には
                  removed_tag_labels フィールドが無い (Contract)。 */}
                <WorkflowCaseTagEditor
                  inputMaxWidth="280px"
                  onChange={setDefaultTags}
                  onRemovedTagLabelsChange={
                    isCreate ? undefined : setRemovedTagLabels
                  }
                  removedTagLabels={isCreate ? undefined : removedTagLabels}
                  suggestedTags={suggestedTags}
                  tags={defaultTags}
                  templateHeadings
                />
              </FieldRoot>

              {/* 大ステップエディタ */}
              <BigStepEditor
                approvalProcesses={approvalProcesses}
                control={control}
                errors={errors}
                getValues={getValues}
                members={members}
                setValue={setValue}
                watch={watch}
              />

              {/* 監査情報（編集時のみ） */}
              {!isCreate && fetched !== undefined && (
                <AuditPanel
                  createdAt={
                    fetched.createdAt !== undefined
                      ? timestampDate(fetched.createdAt)
                      : undefined
                  }
                  createdBy={fetched.createdBy}
                  updatedAt={
                    fetched.updatedAt !== undefined
                      ? timestampDate(fetched.updatedAt)
                      : undefined
                  }
                  updatedBy={fetched.updatedBy}
                />
              )}

              {/* フォームアクション (design .ct-form-actions: 右寄せ + 上 border) */}
              <Flex
                alignItems="center"
                justifyContent="flex-end"
                gap="8px" // space-2
                paddingTop="12px" // space-3
                borderTopWidth="1px"
                borderTopColor="Border.lowEmphasis"
              >
                {cancelButton}
                {saveButton}
              </Flex>
            </>
          )}
        </RegionLoading>
      </VStack>
    </Box>
  )
}
